import { services } from "@services";
import { getServiceLanguage, languageServiceMap } from "@services/languages";

export const BASE_URL = "https://bluesbrackets.com";

export function normalizePath(path: string): string {
  const withoutQuery = path.split(/[?#]/)[0];

  if (!withoutQuery || withoutQuery === "/") {
    return "";
  }

  return withoutQuery.replace(/\/$/, "");
}

export function buildAbsoluteUrl(path: string): string {
  const normalized = normalizePath(path);

  if (!normalized) {
    return `${BASE_URL}/`;
  }

  return `${BASE_URL}${normalized}`;
}

function getServiceAlternatePaths(path: string): {
  enPath?: string;
  plPath?: string;
} | null {
  const match = path.match(/^\/services\/([^/]+)$/);

  if (!match) {
    return null;
  }

  const slug = match[1];
  const alternateSlug = languageServiceMap[slug];

  if (alternateSlug) {
    const contentLanguage = getServiceLanguage(slug);

    if (contentLanguage === "en") {
      return {
        enPath: `/services/${slug}`,
        plPath: `/pl/services/${alternateSlug}`,
      };
    }

    return {
      enPath: `/services/${alternateSlug}`,
      plPath: `/pl/services/${slug}`,
    };
  }

  const inEn = Object.prototype.hasOwnProperty.call(services.en, slug);
  const inPl = Object.prototype.hasOwnProperty.call(services.pl, slug);

  if (inEn && !inPl) {
    return { enPath: `/services/${slug}` };
  }

  if (inPl && !inEn) {
    return { plPath: `/pl/services/${slug}` };
  }

  return {
    enPath: `/services/${slug}`,
    plPath: `/pl/services/${slug}`,
  };
}

export function buildLocalizedPageUrls(
  locale: string | undefined,
  asPath: string,
) {
  const normalizedPath = normalizePath(asPath);
  const serviceAlternates = getServiceAlternatePaths(normalizedPath);

  const enPath =
    serviceAlternates?.enPath ??
    (serviceAlternates ? undefined : normalizedPath);
  const plPath =
    serviceAlternates?.plPath ??
    (serviceAlternates
      ? undefined
      : normalizedPath
        ? `/pl${normalizedPath}`
        : "/pl");

  const enUrl = enPath !== undefined ? buildAbsoluteUrl(enPath) : undefined;
  const plUrl = plPath !== undefined ? buildAbsoluteUrl(plPath) : undefined;
  const canonicalUrl =
    locale === "pl" ? (plUrl ?? enUrl ?? `${BASE_URL}/`) : (enUrl ?? plUrl ?? `${BASE_URL}/`);
  const xDefaultUrl = enUrl ?? canonicalUrl;

  return {
    canonicalUrl,
    enUrl,
    plUrl,
    xDefaultUrl,
  };
}
